import type React from "react"

import { useCallback, useEffect, useState, useRef, useMemo } from "react"
import { Command, FileText, Link2, Hash, Search, X } from "lucide-react"
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { FolderPickerDialog } from "@/components/features/folder-picker-dialog"
import { cn } from "@/lib/utils"
import { testIds } from "@/lib/test-ids"
import type { CommandItem, CommandPaletteProps } from "./command-palette.types"
import { GoogleDriveImportRowChrome } from "./command-palette.google-drive"
import {
  queryOptions,
  getSearchHistory,
  addToSearchHistory,
  extractFilenameFromPath,
  extractBasecampIdFromFilename,
  extractBasecampId,
  highlightPathMatch,
  COMMAND_PALETTE_BASECAMP_URL_KEY,
  getDocumentIcon,
} from "./command-palette.utils"
import { executeCommandPaletteSearch } from "./command-palette.search"
import { getPrefetchedDocumentMetadata } from "@/hooks/use-document-search-boost-ids"
import { useCommandPaletteKeyboard } from "./command-palette.keyboard.hooks"
import { useBasecampHandlers } from "./command-palette.basecamp"
import { WorkspaceSearchBadge } from "./workspace-search-badge"
import { WorkspaceScopeSelector } from "./workspace-scope-selector"
import { compactSearchMatch } from "@/lib/compact-search-match"
import { parseQuery, MODE_LABELS } from "@/lib/search/query-parser"
import { fetchWithAuth } from "@/lib/api"
import { toast } from "sonner"
import { clearUserHiddenWorkspace } from "@/lib/user-hidden-workspaces"
import { isMaaterAppRouteQuery, isMaaterDocumentUrlQuery } from "@/lib/maater-document-url"
import { dispatchOpenInboxMain } from "@/components/features/inbox/inbox-folder-types"
import { useNavigate } from "react-router-dom"
import { isGoogleDriveUrlQuery } from "@/lib/google-drive-url"
import { openSidebarMode } from "@/lib/sidebar-mode"
import { folderLabelForCreatePath } from "@/lib/resolve-create-folder-path"
import {
  importDriveFilesSequentially,
  resolveDriveImportDestination,
} from "@/components/integrations/google-workspace/gdoc-import-utils"
import { useWorkspace } from "@/providers/workspace-provider"

export function CommandPalette({
  open: controlledOpen,
  onOpenChange,
  initialQuery = "",
  onDocumentSelect,
  openTabDocumentIds = [],
  recentlyViewedDocumentIds = [],
  folderPaths,
  workspaces = [],
  activeWorkspaceIds = [],
  selectedProjectId = null,
  openTabs = [],
  activeTabId = null,
}: CommandPaletteProps = {}) {
  const { setActiveWorkspaceIds } = useWorkspace()
  const navigate = useNavigate()
  const [internalOpen, setInternalOpen] = useState(false)
  const [search, setSearch] = useState(initialQuery)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [results, setResults] = useState<CommandItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [hasDocumentMatches, setHasDocumentMatches] = useState(false)
  /** null = use global folderPaths from props; non-null = per-session workspace ID set */
  const [paletteScopeIds, setPaletteScopeIds] = useState<string[] | null>(null)

  const searchableWorkspaces = useMemo(
    () => workspaces.filter((w) => w.contentRoot),
    [workspaces],
  )

  const effectiveFolderPaths = useMemo(() => {
    if (paletteScopeIds === null) {
      return folderPaths
    }
    const paths = searchableWorkspaces
      .filter((w) => paletteScopeIds.includes(w.id))
      .map((w) => w.contentRoot as string)
    return paths.length > 0 ? paths : undefined
  }, [paletteScopeIds, folderPaths, searchableWorkspaces])

  const effectiveScopeIds = useMemo(() => {
    if (paletteScopeIds !== null) return paletteScopeIds
    return activeWorkspaceIds
  }, [paletteScopeIds, activeWorkspaceIds])

  const scopeSummary = useMemo(() => {
    const names = effectiveScopeIds
      .map((id) => workspaces.find((w) => w.id === id)?.name)
      .filter((name): name is string => Boolean(name))
    if (names.length === 0) return "All workspaces"
    const label = names.length <= 2 ? names.join(", ") : `${names.length} workspaces`
    return paletteScopeIds === null ? label : `${label} (this search)`
  }, [effectiveScopeIds, workspaces, paletteScopeIds])

  const [driveFolderPickerOpen, setDriveFolderPickerOpen] = useState(false)
  const [pendingDriveImport, setPendingDriveImport] = useState<CommandItem | null>(null)

  const activeTab = useMemo(
    () => openTabs.find((tab) => tab.id === activeTabId),
    [openTabs, activeTabId],
  )

  const driveImportDest = useMemo(
    () =>
      resolveDriveImportDestination({
        activeTab,
        selectedProjectId,
        fallbackOrganizationId: effectiveScopeIds[0] || workspaces[0]?.id,
        workspaces,
      }),
    [activeTab, selectedProjectId, effectiveScopeIds, workspaces],
  )

  const driveImportFolderLabel = folderLabelForCreatePath(driveImportDest.folderPath, 'folder')

  // Search history state
  const [searchHistory, setSearchHistory] = useState<string[]>([])
  const [historyIndex, setHistoryIndex] = useState(-1) // -1 means not in history mode
  const [currentInput, setCurrentInput] = useState("") // Store current input when browsing history

  // Input ref for manual focus control
  const inputRef = useRef<HTMLInputElement>(null)
  const searchGenerationRef = useRef(0)
  const searchAbortRef = useRef<AbortController | null>(null)

  // Use controlled or uncontrolled state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen
  const setOpen = onOpenChange || setInternalOpen

  // Load search history when palette opens and focus input
  useEffect(() => {
    if (open) {
      const history = getSearchHistory()
      console.log('[Search History] Loading history on palette open:', {
        historyCount: history.length,
        history
      })
      setSearchHistory(history)

      // Focus the input field after a short delay to ensure dialog is rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
          console.log('[Search History] Input focused')
        }
      }, 100)
    } else {
      console.log('[Search History] Palette closed, resetting state')
      setPaletteScopeIds(null)
    }
  }, [open])

  // Trim search input for consistent processing
  const trimmedSearch = search.trim()

  // Parse query for prefix mode (file:, >, @, #, tag:, query:)
  const parsedQuery = useMemo(() => parseQuery(search), [search])

  // Chat-promotion state: 'current' keeps existing thread, 'new' starts fresh
  const [currentChatTarget, setCurrentChatTarget] = useState<'current' | 'new'>('current')

  // Use Basecamp handlers hook
  const { handleBasecampUpdate, handleBasecampTodoIngestion, handleBasecampMessageIngestion } = useBasecampHandlers({
    setOpen,
    onDocumentSelect,
    setLoading,
    trimmedSearch,
    activeWorkspaceIds,
    onAddWorkspaceToScope: (workspaceId) => {
      if (!activeWorkspaceIds.includes(workspaceId)) {
        setActiveWorkspaceIds([...activeWorkspaceIds, workspaceId])
      }
    },
  })

  const openChatWithPrompt = (query: string, opts?: { newThread?: boolean }) => {
    window.dispatchEvent(
      new CustomEvent('flownotes:open-chat-prompt', {
        detail: { query, newThread: opts?.newThread ?? false },
      }),
    )
  }

  const extractedFilename = extractFilenameFromPath(trimmedSearch)
  const basecampIdFromFilename = extractedFilename ? extractBasecampIdFromFilename(extractedFilename) : null
  const basecampInfo = extractBasecampId(trimmedSearch)
  const basecampId = basecampInfo.id
  const basecampType = basecampInfo.type
  const isUrl = trimmedSearch.startsWith("http://") || trimmedSearch.startsWith("https://")
  const isMaaterDocUrl = isMaaterDocumentUrlQuery(trimmedSearch)
  const isMaaterAppUrl = isMaaterAppRouteQuery(trimmedSearch)
  const isDriveUrl = isGoogleDriveUrlQuery(trimmedSearch)
  const hideExternalUrlRow = isMaaterDocUrl || isMaaterAppUrl || isDriveUrl
  // Must be boolean — `.match()` returns a new array each call and would destabilize useEffect deps.
  const isId =
    trimmedSearch.startsWith("#") || /^[a-zA-Z0-9-_]+$/.test(trimmedSearch)

  // Detect numeric IDs (like Basecamp IDs) - prioritize filename search
  const isNumericId = /^\d+$/.test(trimmedSearch)
  const isBasecampId = !!basecampId // Use extracted ID instead
  const isBareBasecampTodoId = isNumericId && basecampType === "todo" && !isUrl

  const optimisticIngestItem = useMemo((): CommandItem | null => {
    if (!isBareBasecampTodoId || !basecampId) return null
    return {
      id: `ingest-basecamp-${basecampId}`,
      title: `Ingest Basecamp Todo ${basecampId}`,
      type: "action",
      icon: <Hash className="h-4 w-4" />,
      subtitle: "Create new markdown file from Basecamp todo",
      action: "ingest-basecamp-todo",
      matchScore: 80,
      basecampId,
      basecampType: "todo",
    }
  }, [isBareBasecampTodoId, basecampId])

  const displayResults = useMemo(() => {
    if (!optimisticIngestItem) return results
    const hasIngest = results.some((r) => r.action === "ingest-basecamp-todo")
    if (hasIngest) return results
    return [optimisticIngestItem, ...results]
  }, [optimisticIngestItem, results])

  // Detect structured query patterns
  const isStructuredQuery = queryOptions.some(option => trimmedSearch.startsWith(option.label))
  const currentQueryType = queryOptions.find(option => trimmedSearch.startsWith(option.label))

  // Helper: build CommandItems from prefetched document metadata
  const buildRecentsItems = (
    docs: ReturnType<typeof getPrefetchedDocumentMetadata>,
    priorityIds: string[],
    subtitlePrefix: string,
    limit: number,
  ): CommandItem[] => {
    const orderMap = new Map(priorityIds.map((id, i) => [id, i]))
    return [...docs]
      .sort((a, b) => (orderMap.get(a.id) ?? 999) - (orderMap.get(b.id) ?? 999))
      .slice(0, limit)
      .map((doc) => {
        const filename = doc.filePath ? doc.filePath.split("/").at(-1) : null
        return {
          id: doc.id,
          title: doc.title,
          type: "file" as const,
          icon: getDocumentIcon(doc.type),
          subtitle: filename ? `${subtitlePrefix} • ${filename}` : subtitlePrefix,
          action: "open-document",
          matchScore: 0,
          docType: doc.type,
          filePath: doc.filePath,
        }
      })
  }

  // Real search instead of mock data
  useEffect(() => {
    if (!trimmedSearch) {
      // Fix B: show recently-viewed docs immediately when palette opens with no query
      const prefetched = getPrefetchedDocumentMetadata()
      if (prefetched.length > 0) {
        const recents = buildRecentsItems(prefetched, recentlyViewedDocumentIds, "Recently viewed", 10)
        setResults(recents)
        setHasDocumentMatches(recents.length > 0)
      } else {
        setResults([])
        setHasDocumentMatches(false)
      }
      setSearchError(null)
      setLoading(false)
      return
    }

    // Fix C: instant client-side filename pre-results before the debounced API call
    const parsedQ = parseQuery(trimmedSearch)
    const isFileMode = parsedQ.mode === "file"
    if (isFileMode && parsedQ.terms) {
      const terms = parsedQ.terms.toLowerCase()
      const prefetched = getPrefetchedDocumentMetadata()
      const clientMatches = prefetched
        .filter((doc) => {
          const filename = doc.filePath?.split("/").at(-1) ?? ""
          return compactSearchMatch(filename, terms) || compactSearchMatch(doc.title, terms)
        })
        .slice(0, 8)
        .map((doc) => ({
          id: doc.id,
          title: doc.title,
          type: "file" as const,
          icon: getDocumentIcon(doc.type),
          subtitle: `📄 ${doc.filePath?.split("/").at(-1) ?? doc.type}`,
          action: "open-document",
          matchScore: 80,
          matchType: "filename",
          filePath: doc.filePath,
          docType: doc.type,
        }))
      if (clientMatches.length > 0) {
        setResults(clientMatches)
        setHasDocumentMatches(true)
      }
    }

    const timeoutId = setTimeout(async () => {
      searchAbortRef.current?.abort()
      const controller = new AbortController()
      searchAbortRef.current = controller
      const generation = ++searchGenerationRef.current

      setLoading(true)
      setSearchError(null)

      try {
        const result = await executeCommandPaletteSearch({
          trimmedSearch,
          isUrl,
          isId,
          isNumericId,
          isBasecampId,
          extractedFilename,
          basecampIdFromFilename,
          basecampId,
          basecampType,
          openTabDocumentIds,
          recentlyViewedDocumentIds,
          folderPaths: effectiveFolderPaths,
          workspaceIds: effectiveScopeIds.length > 0 ? effectiveScopeIds : undefined,
          activeWorkspaceIds,
          workspaces,
          signal: controller.signal,
          isCurrentSearch: () => searchGenerationRef.current === generation,
        })
        if (searchGenerationRef.current !== generation) {
          return
        }
        setResults(result.allResults)
        setHasDocumentMatches(result.hasDocumentMatches)
        setSearchError(result.searchError ?? null)
        if (result.preferredDocumentIndex != null && result.preferredDocumentIndex >= 0) {
          setSelectedIndex(result.preferredDocumentIndex)
        } else if (result.autoSelectFirstFilenameMatch) {
          const firstIndex = (isUrl && !hideExternalUrlRow ? 1 : 0) + (isId ? 1 : 0)
          setSelectedIndex(firstIndex)
        }
      } catch (err) {
        if (searchGenerationRef.current !== generation) {
          return
        }
        console.error('Command palette search failed:', err)
        setResults([])
        setHasDocumentMatches(false)
        setSearchError(err instanceof Error ? err.message : 'Search failed')
      } finally {
        if (searchGenerationRef.current === generation) {
          setLoading(false)
        }
      }
    }, isUrl ? 50 : 200)

    return () => {
      clearTimeout(timeoutId)
      searchAbortRef.current?.abort()
    }
  }, [
    search,
    isNumericId,
    extractedFilename,
    basecampId,
    basecampType,
    isBasecampId,
    basecampIdFromFilename,
    isUrl,
    isId,
    hideExternalUrlRow,
    isDriveUrl,
    openTabDocumentIds,
    recentlyViewedDocumentIds,
    effectiveFolderPaths,
    effectiveScopeIds,
    activeWorkspaceIds,
    workspaces,
  ])


  // Show query options when typing a query prefix
  // DISABLED: These take up too much space and push down real results
  // TODO: Re-enable when backend fully supports structured queries (tag:, status:, etc.)
  const showQueryOptions = false // search.length > 0 && !isStructuredQuery && !isUrl && !isId

  const handleSelectRef = useRef<(item: CommandItem, event?: React.MouseEvent) => void>(() => {})
  const handleSpecialSearchRef = useRef<() => void>(() => {})

  const runPaletteDriveImport = useCallback(
    async (item: CommandItem, folderPath?: string) => {
      const dest = folderPath
        ? resolveDriveImportDestination({
            explicitFolderPath: folderPath,
            activeTab,
            selectedProjectId,
            fallbackOrganizationId: effectiveScopeIds[0] || workspaces[0]?.id,
            workspaces,
          })
        : driveImportDest
      const organizationId = dest.organizationId
      const fileId = item.driveFileId
      if (!organizationId || !fileId) {
        toast.error('Cannot import Google Doc', {
          description: 'Missing workspace or Drive file id.',
        })
        return
      }
      const folderLabel = folderLabelForCreatePath(dest.folderPath, 'folder')
      const toastId = toast.loading(`Importing to ${folderLabel}...`)
      try {
        const results = await importDriveFilesSequentially({
          organizationId,
          folderPath: dest.folderPath,
          files: [
            {
              fileId,
              name: item.driveFileName || item.title.replace(/^Import /, ''),
              mimeType: item.driveMimeType || 'application/vnd.google-apps.document',
            },
          ],
        })
        const first = results[0]
        if (first?.status === 'error' || !first?.documentId) {
          toast.error('Import failed', {
            id: toastId,
            description: first?.error || 'No document was created.',
          })
          return
        }
        toast.success(`Imported to ${folderLabel}`, { id: toastId })
        if (onDocumentSelect && first.documentId) {
          onDocumentSelect(first.documentId)
        }
      } catch (err) {
        toast.error('Import failed', {
          id: toastId,
          description: err instanceof Error ? err.message : undefined,
        })
      }
    },
    [
      activeTab,
      selectedProjectId,
      effectiveScopeIds,
      workspaces,
      driveImportDest,
      onDocumentSelect,
    ],
  )

  // Reset state when closing
  useEffect(() => {
    if (!open) {
      setSearch(initialQuery)
      setSelectedIndex(0)
      setHistoryIndex(-1)
      setCurrentInput("")
    }
  }, [open, initialQuery])


  const handleSelect = (item: CommandItem, event?: React.MouseEvent) => {
    console.log("[Command Palette] Selected item:", item)

    // Add current search to history (if meaningful)
    if (search.trim()) {
      console.log('[Search History] Saving search to history:', search.trim())
      addToSearchHistory(search.trim())
      // Verify it was saved
      const updatedHistory = getSearchHistory()
      console.log('[Search History] Updated history after save:', updatedHistory)
    } else {
      console.log('[Search History] Not saving empty search')
    }

    // Handle document selection
    if (item.action === 'open-document' && onDocumentSelect) {
      const urlHint = item.basecampSourceUrl || (trimmedSearch.includes('basecamp.com') ? trimmedSearch : null)
      if (urlHint) {
        try {
          sessionStorage.setItem(COMMAND_PALETTE_BASECAMP_URL_KEY, urlHint)
        } catch {
          // ignore quota / private mode
        }
      }
      console.log("[Command Palette] Opening document:", item.id, item.title, "CMD+Click:", event?.metaKey || event?.ctrlKey)
      onDocumentSelect(item.id, event)
    } else if (item.action === 'open-basecamp-todo') {
      const todoId = item.basecampId
      const openIntegrationTab = (projectId: string) => {
        setOpen(false)
        window.dispatchEvent(new CustomEvent('integration-view-change', {
          detail: {
            tabId: `basecamp-todo-${todoId}`,
            title: 'Loading...',
            type: 'integration-basecamp-todo',
            integrationData: {
              integrationId: 'basecamp',
              projectId,
              todoId,
            },
          },
        }))
      }

      if (todoId && item.basecampProjectId) {
        openIntegrationTab(item.basecampProjectId)
      } else if (todoId) {
        void (async () => {
          try {
            const res = await fetchWithAuth(`/api/basecamp/search-todo/${todoId}`)
            if (!res.ok) {
              toast.error('Cannot open todo', {
                description: 'Todo not in cache. Open it in Basecamp first or paste the full URL.',
              })
              return
            }
            const data = await res.json()
            const projectId = data.todo?.bucketId || data.todo?.projectId
            if (projectId) {
              openIntegrationTab(String(projectId))
            } else {
              toast.error('Cannot open todo', { description: 'Project ID not found for this todo.' })
            }
          } catch {
            toast.error('Cannot open todo', { description: 'Lookup failed.' })
          }
        })()
      }
    } else if (item.action === 'open-project' && onDocumentSelect) {
      // For projects, we could implement project selection logic here
      console.log("[Command Palette] Project selection not yet implemented:", item.id)
    } else if (item.action === 'ingest-basecamp-todo') {
      // Handle Basecamp todo ingestion (new todo)
      console.log("[Command Palette] Ingesting Basecamp todo:", item.basecampId)
      if (item.basecampId) {
        handleBasecampTodoIngestion(item.basecampId)
      }
    } else if (item.action === 'ingest-basecamp-message') {
      // Handle Basecamp message/inbox forward ingestion (new message)
      console.log("[Command Palette] Ingesting Basecamp message:", item.basecampId, "type:", item.basecampType, "full item:", item)
      if (item.basecampId && item.basecampType) {
        handleBasecampMessageIngestion(item.basecampId, item.basecampType)
      } else {
        console.error("[Command Palette] Missing basecampId or basecampType:", { basecampId: item.basecampId, basecampType: item.basecampType })
      }
    } else if (item.action === 'update-basecamp-todo') {
      // Handle Basecamp todo update (existing work spec)
      console.log("[Command Palette] Updating Basecamp todo:", item.basecampId, "doc:", item.existingDocId)
      if (item.basecampId && item.existingDocId) {
        handleBasecampUpdate(item.basecampId, item.existingDocId, 'todo')
      }
    } else if (item.action === 'update-basecamp-message') {
      // Handle Basecamp message update (existing message)
      console.log("[Command Palette] Updating Basecamp message:", item.basecampId, "doc:", item.existingDocId, "type:", item.basecampType)
      if (item.basecampId && item.existingDocId) {
        handleBasecampUpdate(item.basecampId, item.existingDocId, item.basecampType || 'message')
      }
    } else if (item.action === 'enable-workspace-and-open') {
      const wsId = item.enableWorkspaceId
      const openId = item.existingDocId || item.filePath
      if (wsId) {
        clearUserHiddenWorkspace(wsId)
        if (!activeWorkspaceIds.includes(wsId)) {
          setActiveWorkspaceIds([...activeWorkspaceIds, wsId])
        }
        toast.success(`Enabled ${item.enableWorkspaceName || 'workspace'}`, {
          description: openId ? 'Opening document…' : undefined,
        })
      }
      if (openId && onDocumentSelect) {
        onDocumentSelect(openId, event)
      }
    } else if (item.action === 'import-google-drive') {
      void runPaletteDriveImport(item)
    } else if (item.action === 'open-google-drive-sidebar') {
      openSidebarMode('drive')
      toast.message('Open Google Drive in the sidebar to connect or browse')
    } else if (item.action === 'open-inbox-thread' && item.inboxThreadId) {
      window.dispatchEvent(
        new CustomEvent('open-inbox-thread-main', {
          detail: {
            threadId: item.inboxThreadId,
            title: item.title.replace(/^Open\s+/, ''),
            clickEvent: event,
          },
        }),
      )
      openSidebarMode('inbox')
    } else if (item.action === 'open-inbox') {
      openSidebarMode('inbox')
      dispatchOpenInboxMain()
    } else if (item.action === 'open-chat-conversation' && item.chatThreadId) {
      window.dispatchEvent(
        new CustomEvent('open-chat-conversation-main', {
          detail: {
            threadId: item.chatThreadId,
            title: item.title.replace(/^Open\s+/, ''),
            clickEvent: event,
          },
        }),
      )
    } else if (item.action === 'open-app-path' && item.routePathname) {
      navigate(item.routePathname)
    } else {
      console.log("[Command Palette] No handler for action:", item.action, "onDocumentSelect:", !!onDocumentSelect)
    }

    setOpen(false)
  }

  const handleSpecialSearch = () => {
    const raw = trimmedSearch.replace(/^#/, "")

    // Maater document URLs use the Open document row — do not open a new browser tab.
    if (isUrl && !trimmedSearch.includes("basecamp.com") && !hideExternalUrlRow) {
      window.open(trimmedSearch, "_blank", "noopener,noreferrer")
      setOpen(false)
      return
    }

    // Document UUID prefix search (backend id: branch)
    if (/^[0-9a-f]{8,}$/i.test(raw)) {
      setSearch(`id:${raw}`)
      setSelectedIndex(0)
      return
    }

    setOpen(false)
  }

  handleSelectRef.current = handleSelect
  handleSpecialSearchRef.current = handleSpecialSearch

  useCommandPaletteKeyboard({
    open,
    inputRef,
    search,
    trimmedSearch,
    selectedIndex,
    setSelectedIndex,
    setSearch,
    displayResults,
    isUrl,
    isMaaterDocUrl: hideExternalUrlRow,
    isDriveUrl,
    isId,
    showQueryOptions,
    searchHistory,
    historyIndex,
    setHistoryIndex,
    currentInput,
    setCurrentInput,
    parsedQuery,
    currentChatTarget,
    setCurrentChatTarget,
    setOpen,
    openChatWithPrompt,
    handleSelectRef,
    handleSpecialSearchRef,
  })

  return (
    <>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="p-0 gap-0 max-w-2xl overflow-hidden top-[20vh] translate-y-0"
        showCloseButton={false}
        data-testid={testIds.commandPalette.container}
        aria-label="Command palette"
      >
        <DialogTitle className="sr-only">Command Palette</DialogTitle>
        <DialogDescription className="sr-only">
          Search for documents, projects, or enter Basecamp IDs
        </DialogDescription>
        <div className="flex items-center border-b border-border px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground mr-2 flex-shrink-0" aria-hidden="true" />
          <Input
            ref={inputRef}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearch(e.target.value)
              setSelectedIndex(0)
              // Exit history mode when user types
              if (historyIndex !== -1) {
                setHistoryIndex(-1)
                setCurrentInput("")
              }
            }}
            placeholder={historyIndex > -1 ? `History ${historyIndex + 1}/${searchHistory.length}` : "Search files, paste URLs, or enter queries..."}
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-10 flex-1"
            autoFocus
            aria-label="Command palette search"
            data-testid={testIds.commandPalette.input}
          />
          <div className="flex items-center gap-1 text-xs text-muted-foreground ml-3 flex-shrink-0">
            {historyIndex > -1 && (
              <span className="text-[10px] text-primary mr-2">
                {historyIndex + 1}/{searchHistory.length}
              </span>
            )}
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium">
              <Command className="h-3 w-3" />K
            </kbd>
          </div>
        </div>

        {searchableWorkspaces.length > 0 && (
          <div
            className="flex items-center gap-2 border-b border-border px-3 py-1 bg-muted/20"
            data-testid={testIds.commandPalette.scopeRow}
          >
            <span className="text-[10px] text-muted-foreground shrink-0">Search scope</span>
            <span className="min-w-0 truncate text-[10px] text-foreground/80">
              {scopeSummary}
            </span>
            <WorkspaceScopeSelector
              scope={paletteScopeIds}
              onScopeChange={setPaletteScopeIds}
              fallbackScope={activeWorkspaceIds}
              label="Search scope"
              description="Filter results to specific workspaces."
              triggerClassName="ml-auto h-6 text-xs"
              triggerTestId={testIds.commandPalette.scopeTrigger}
            />
          </div>
        )}

        {/* Prefix mode chip bar */}
        {parsedQuery.mode !== 'default' && (
          <div
            className="border-b border-border px-3 py-1 flex items-center gap-1.5"
            data-testid={testIds.commandPalette.prefixChipBar}
          >
            <span className="text-[10px] text-muted-foreground">Mode:</span>
            <span className="text-[10px] font-medium rounded-md border px-1.5 py-0.5 border-primary text-primary flex items-center gap-1">
              {MODE_LABELS[parsedQuery.mode]}
              <button
                type="button"
                aria-label="Clear prefix mode"
                className="ml-0.5 opacity-70 hover:opacity-100"
                onClick={() => {
                  setSearch(parsedQuery.terms)
                  setSelectedIndex(0)
                  inputRef.current?.focus()
                }}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
            <span className="text-[10px] text-muted-foreground ml-auto">Press Esc to clear</span>
          </div>
        )}

        <div
          className="max-h-[400px] overflow-y-auto p-2"
          data-testid={testIds.commandPalette.results}
          role="listbox"
          aria-label="Search results"
        >
          {/* Query Options */}
          {showQueryOptions && (
            <div className="mb-2">
              <div className="text-xs text-muted-foreground px-2 py-1 font-medium">Search options</div>
              {queryOptions.map((option, index) => (
                <div
                  key={option.key}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors",
                    index === selectedIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50",
                  )}
                  onClick={() => {
                    setSearch(option.label)
                    setSelectedIndex(0)
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="text-muted-foreground">{option.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{option.label}</div>
                    <div className="text-xs text-muted-foreground">{option.description}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* External URL only — Maater / Drive URLs promote Open or Import instead */}
          {isUrl && !trimmedSearch.includes('basecamp.com') && !hideExternalUrlRow && (
            <div
              className="flex items-center gap-3 px-3 py-2 rounded-md bg-accent/50 cursor-pointer hover:bg-accent mb-2"
              onClick={handleSpecialSearch}
            >
              <Link2 className="h-4 w-4 text-primary" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">Open URL</div>
                <div className="text-xs text-muted-foreground truncate">{search}</div>
              </div>
            </div>
          )}

          {/* ID Search - document UUID prefix; hide for bare Basecamp todo IDs (ingest row covers those) */}
          {isId && !isUrl && !isBareBasecampTodoId && search.length > 2 && !hasDocumentMatches && (
            <div
              className="flex items-center gap-3 px-3 py-2 rounded-md bg-accent/50 cursor-pointer hover:bg-accent mb-2"
              onClick={handleSpecialSearch}
            >
              <Hash className="h-4 w-4 text-primary" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">Search by ID</div>
                <div className="text-xs text-muted-foreground truncate">{search}</div>
              </div>
            </div>
          )}

          {/* Path Detection Info - Only show while loading */}
          {extractedFilename && loading && (
            <div className="mb-2 px-3 py-2 rounded-md bg-blue-500/10 border border-blue-500/20">
              <div className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-blue-500" />
                <span className="font-medium text-blue-500">Searching for filename:</span>
                <span className="text-foreground font-mono">{extractedFilename}</span>
              </div>
            </div>
          )}

          {/* Structured Query Info */}
          {isStructuredQuery && currentQueryType && (
            <div className="mb-2 px-3 py-2 rounded-md bg-primary/10 border border-primary/20">
              <div className="flex items-center gap-2 text-sm">
                {currentQueryType.icon}
                <span className="font-medium">{currentQueryType.label}</span>
                <span className="text-muted-foreground">{currentQueryType.description}</span>
              </div>
            </div>
          )}

          {/* Loading State - hide when optimistic ingest is already actionable */}
          {loading && !optimisticIngestItem && (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground"></div>
              <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
            </div>
          )}

          {loading && optimisticIngestItem && (
            <div className="mb-2 px-3 py-1.5 text-xs text-muted-foreground flex items-center gap-2">
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground"></div>
              Searching for existing work specs...
            </div>
          )}

          {/* Search error (API 500/503 or timeout) */}
          {!loading && searchError && (
            <div
              className="mb-2 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/20 text-sm text-red-600 dark:text-red-400"
              data-testid={testIds.commandPalette.searchError}
            >
              Search failed: {searchError}
            </div>
          )}

          {/* Recently viewed header (shown when search is empty and recents exist) */}
          {!trimmedSearch && displayResults.length > 0 && !loading && (
            <div className="px-2 pb-1 text-xs font-medium text-muted-foreground">Recently viewed</div>
          )}

          {/* Regular Results */}
          {!loading && displayResults.length === 0 && !isUrl && !isId && !showQueryOptions && search && !searchError ? (
            <div className="py-8 text-center text-sm text-muted-foreground">No results found for "{search}"</div>
          ) : (displayResults.length > 0 || (!loading && search)) && (
            <div
              className="space-y-1"
              data-testid={testIds.commandPalette.results}
              role="listbox"
              aria-label="Search results"
            >
              {displayResults.map((item, index) => {
                const actualIndex =
                  (showQueryOptions ? queryOptions.length : 0) +
                  (isUrl && !hideExternalUrlRow ? 1 : 0) +
                  (isId ? 1 : 0) +
                  index

                // Use DocumentListItem for file/document results
                if (item.type === 'file' && item.action === 'open-document') {
                  const isFilenameMatch = item.matchType === 'filename' || item.matchType === 'basecamp-id'
                  const isFirstResult = index === 0
                  const showReturnHint = isFilenameMatch && isFirstResult && (extractedFilename || /\.(md|markdown)$/i.test(trimmedSearch))

                  return (
                    <div
                      key={item.id}
                      className={cn(
                        "rounded-md cursor-pointer transition-colors relative",
                        actualIndex === selectedIndex ? "bg-accent" : "hover:bg-accent/50",
                        isFilenameMatch && isFirstResult && "ring-1 ring-blue-500/50"
                      )}
                      onClick={(e) => handleSelect(item, e)}
                      onMouseEnter={() => setSelectedIndex(actualIndex)}
                      role="option"
                      aria-selected={actualIndex === selectedIndex}
                      data-testid={testIds.commandPalette.result(index)}
                    >
                      <div className="px-3 py-2">
                        <div className="flex items-start gap-3">
                          <div className="flex shrink-0 items-start gap-1.5 mt-0.5">
                            {item.workspaceBadge && (
                              <WorkspaceSearchBadge
                                abbreviation={item.workspaceBadge.abbreviation}
                                color={item.workspaceBadge.color}
                                name={item.workspaceBadge.name}
                              />
                            )}
                            <div className="text-muted-foreground">{item.icon}</div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{item.title}</div>
                            {item.filePath && (
                              <div className="mt-1 text-xs text-muted-foreground">
                                {highlightPathMatch(item.filePath, trimmedSearch)}
                              </div>
                            )}
                            {!item.filePath && item.subtitle && (
                              <div className="mt-1 text-xs text-muted-foreground truncate">
                                {item.subtitle}
                              </div>
                            )}
                            {showReturnHint && (
                              <div className="mt-1 text-xs text-blue-500/70 flex items-center gap-1">
                                <kbd className="pointer-events-none inline-flex h-4 select-none items-center gap-0.5 rounded border border-blue-500/30 bg-blue-500/10 px-1 font-mono text-[10px] text-blue-500/70">
                                  ↵
                                </kbd>
                                <span>Return to open</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                }

                // Keep original rendering for actions, projects, etc.
                const isDriveImport = item.action === 'import-google-drive'
                return (
                  <div
                    key={item.id}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors",
                      actualIndex === selectedIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50",
                    )}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setSelectedIndex(actualIndex)}
                    role="option"
                    aria-selected={actualIndex === selectedIndex}
                    data-testid={testIds.commandPalette.result(index)}
                  >
                    <div className="text-muted-foreground">{item.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{item.title}</div>
                      {item.subtitle && (
                        <div className="text-xs text-muted-foreground">
                          {isDriveImport ? (
                            `Import to ${driveImportFolderLabel}`
                          ) : item.isBasecampMatch && basecampId ? (
                            <span>
                              {item.subtitle.replace(
                                new RegExp(`(${basecampId})`, 'g'),
                                '<strong>$1</strong>'
                              ).split('<strong>').map((part, i) =>
                                part.includes('</strong>') ? (
                                  <span key={i}>
                                    <strong>{part.split('</strong>')[0]}</strong>
                                    {part.split('</strong>')[1]}
                                  </span>
                                ) : (
                                  <span key={i}>{part}</span>
                                )
                              )}
                            </span>
                          ) : (
                            item.subtitle
                          )}
                        </div>
                      )}
                    </div>
                    {isDriveImport && (
                      <GoogleDriveImportRowChrome
                        folderLabel={driveImportFolderLabel}
                        onImport={() => handleSelect(item)}
                        onChooseFolder={() => {
                          if (search.trim()) addToSearchHistory(search.trim())
                          setPendingDriveImport(item)
                          setDriveFolderPickerOpen(true)
                          setOpen(false)
                        }}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Pinned AI chat-promotion row */}
          {trimmedSearch.length > 0 && (
            <div
              role="option"
              data-testid={testIds.commandPalette.chatPromptRow}
              className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-sm text-muted-foreground hover:bg-accent border-t border-border mt-1 pt-2"
              onClick={() => {
                openChatWithPrompt(trimmedSearch, { newThread: currentChatTarget === 'new' })
                setOpen(false)
              }}
            >
              <span className="text-base">💬</span>
              <span>Ask agent: "<span className="text-foreground">{trimmedSearch}</span>"</span>
              <kbd className="ml-auto text-[10px] border border-border rounded px-1">⇥ Tab</kbd>
              <span className="text-[10px] text-muted-foreground">{currentChatTarget === 'new' ? 'new chat' : 'current chat'}</span>
            </div>
          )}
        </div>

        <div className="border-t border-border px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px]">
                ↑↓
              </kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px]">
                ↵
              </kbd>
              Select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px]">
                esc
              </kbd>
              Close
            </span>
          </div>
          <div className="text-muted-foreground/60">Try: tag:, file:, path:, project:</div>
        </div>
      </DialogContent>
    </Dialog>
    <FolderPickerDialog
      open={driveFolderPickerOpen}
      onOpenChange={(nextOpen) => {
        setDriveFolderPickerOpen(nextOpen)
        if (!nextOpen) setPendingDriveImport(null)
      }}
      currentPath={driveImportDest.folderPath}
      mode="import"
      title="Import destination"
      description="Choose the folder for this Google Doc."
      confirmLabel="Import here"
      workspaces={searchableWorkspaces.map((ws) => ({
        id: ws.id,
        name: ws.name,
        slug: ws.slug,
        contentRoot: ws.contentRoot ?? '',
      }))}
      onSelect={(folderPath) => {
        const item = pendingDriveImport
        setDriveFolderPickerOpen(false)
        setPendingDriveImport(null)
        if (item) void runPaletteDriveImport(item, folderPath)
      }}
    />
    </>
  )
}
